import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Col, Card, Image, Button, Alert, Modal, Form } from 'react-bootstrap';
import './Contacts.css';
import { showContacts, updateContact, delete_contact, getContactGroups } from '../../../helpers';

interface Contact {
    id: string;
    name: string;
    mobile_no: string;
    group_name: string;
    profileUrl: string;
}
interface GroupItem {
    name: string;
    total: number;
}
interface TestContactProps {
    onSuccess?: (msg: string) => void;
    onError?: (msg: string) => void;
}

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 350;
const FALLBACK_AVATAR = 'https://api.skynyx.ai/file_storage/contacts/1770624591.png';

const toContact = (c: any): Contact => ({
    id: String(c.contact_id),
    name: c.contact_name || c.mobile_no || 'Unknown',
    mobile_no: String(c.mobile_no ?? ''),
    group_name: c.group_name || 'Ungrouped',
    profileUrl: c.profile ? `https://api.skynyx.ai/file_storage/${c.company_id}/contacts/${c.profile}` : FALLBACK_AVATAR,
});

/** Page numbers around the current page, e.g. 1 … 4 5 [6] 7 8 … 40 */
const pageWindow = (current: number, last: number): (number | '…')[] => {
    const pages: (number | '…')[] = [];
    const start = Math.max(1, current - 2);
    const end = Math.min(last, current + 2);
    if (start > 1) pages.push(1);
    if (start > 2) pages.push('…');
    for (let p = start; p <= end; p++) pages.push(p);
    if (end < last - 1) pages.push('…');
    if (end < last) pages.push(last);
    return pages;
};

const Test_Contact: React.FC<TestContactProps> = () => {
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [groups, setGroups] = useState<GroupItem[]>([]);
    const [totalAll, setTotalAll] = useState(0);
    const [activeGroup, setActiveGroup] = useState<string>('All');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [editedContact, setEditedContact] = useState<Contact | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const [alertMsg, setAlertMsg] = useState<string | null>(null);
    const [alertVariant, setAlertVariant] = useState<'success' | 'danger' | 'primary'>('success');

    // Ignore responses that arrive after a newer request was made.
    const requestId = useRef(0);

    const fetchContacts = useCallback(async () => {
        const id = ++requestId.current;
        setLoading(true);
        try {
            const response = await showContacts({
                page,
                per_page: PAGE_SIZE,
                search: search || undefined,
                group: activeGroup !== 'All' ? activeGroup : undefined,
            });
            if (id !== requestId.current) return;

            const rows: any[] = response.data?.data ?? [];
            setContacts(rows.map(toContact));
            setTotal(response.data?.meta?.total ?? rows.length);
            setLastPage(response.data?.meta?.last_page ?? 1);
            setError(null);
        } catch {
            if (id !== requestId.current) return;
            setError('Failed to fetch contacts');
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [page, search, activeGroup]);

    const fetchGroups = useCallback(async () => {
        try {
            const response = await getContactGroups();
            setGroups(response.data?.groups ?? []);
            setTotalAll(response.data?.total ?? 0);
        } catch {
            /* sidebar counts are non-critical */
        }
    }, []);

    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    // Debounce typing so every keystroke does not hit the server.
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        if (!alertMsg) return;
        const timer = setTimeout(() => setAlertMsg(null), 3000);
        return () => clearTimeout(timer);
    }, [alertMsg]);

    const filterByGroup = (group: string) => {
        setActiveGroup(group);
        setPage(1);
    };

    const handleDelete = async (contact: Contact) => {
        if (!window.confirm('Are you sure you want to delete this contact?')) return;

        try {
            await delete_contact(contact.id);
            await Promise.all([fetchContacts(), fetchGroups()]);
            setAlertVariant('danger');
            setAlertMsg('Deleted successfully');
        } catch (err) {
            console.error(err);
            setAlertVariant('danger');
            setAlertMsg('Delete failed');
        }
    };

    const handleView = (contact: Contact) => {
        setSelectedContact(contact);
        setShowViewModal(true);
    };

    const handleEdit = (contact: Contact) => {
        setEditedContact({ ...contact });
        setPreviewUrl(null);
        setShowEditModal(true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!editedContact) return;
        setEditedContact({ ...editedContact, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        const reader = new FileReader();
        reader.onloadend = () => setPreviewUrl(reader.result as string);
        reader.readAsDataURL(e.target.files[0]);
    };

    const handleSaveEdit = async () => {
        if (!editedContact) return;

        try {
            await updateContact(editedContact.id, {
                contact_name: editedContact.name,
                mobile_no: editedContact.mobile_no,
                group_name: editedContact.group_name === 'Ungrouped' ? '' : editedContact.group_name,
                profile: previewUrl || editedContact.profileUrl,
            });
            setShowEditModal(false);
            await Promise.all([fetchContacts(), fetchGroups()]);
            setAlertVariant('success');
            setAlertMsg('Contact updated');
        } catch (err) {
            console.error(err);
            setAlertVariant('danger');
            setAlertMsg('Update failed');
        }
    };

    const firstShown = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const lastShown = Math.min(page * PAGE_SIZE, total);

    return (
        <>
            {alertMsg && (
                <Alert variant={alertVariant} onClose={() => setAlertMsg(null)} dismissible className="mb-3">
                    {alertMsg}
                </Alert>
            )}
            {error && <Alert variant="danger">{error}</Alert>}

            <Row>
                {/* LEFT SIDEBAR */}
                <Col md={2}>
                    <Card>
                        <Card.Body>
                            <h6>Groups</h6>

                            <div
                                className={`group-item ${activeGroup === 'All' ? 'active' : ''}`}
                                onClick={() => filterByGroup('All')}
                            >
                                All <span>{totalAll.toLocaleString()}</span>
                            </div>

                            {groups.map((grp) => (
                                <div
                                    key={grp.name}
                                    className={`group-item ${activeGroup === grp.name ? 'active' : ''}`}
                                    onClick={() => filterByGroup(grp.name)}
                                    title={grp.name}
                                >
                                    {grp.name.length > 15 ? grp.name.substring(0, 15) + '...' : grp.name}
                                    <span>{grp.total.toLocaleString()}</span>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </Col>

                {/* RIGHT CONTENT */}
                <Col md={10}>
                    <Card>
                        <Card.Body>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <div className="position-relative flex-grow-1">
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by name or mobile number..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                    />
                                    {searchInput && (
                                        <i
                                            className="bi bi-x-circle-fill text-muted position-absolute"
                                            style={{ right: 12, top: 10, cursor: 'pointer' }}
                                            onClick={() => setSearchInput('')}
                                        />
                                    )}
                                </div>
                                <span className="text-muted text-nowrap" style={{ fontSize: 13 }}>
                                    {loading ? 'Loading…' : `${total.toLocaleString()} contact${total === 1 ? '' : 's'}`}
                                </span>
                            </div>

                            <Row style={{ opacity: loading && contacts.length ? 0.55 : 1, transition: 'opacity .15s' }}>
                                {contacts.map((contact) => (
                                    <Col md={3} key={contact.id} className="">
                                        <Card className="text-center shadow-sm">
                                            <Card.Body>
                                                <Image
                                                    src={contact.profileUrl}
                                                    roundedCircle
                                                    width={80}
                                                    height={80}
                                                    className="mb-2"
                                                    loading="lazy"
                                                    style={{ objectFit: 'cover' }}
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).src = FALLBACK_AVATAR;
                                                    }}
                                                />
                                                <h6 className="text-truncate" title={contact.name}>
                                                    {contact.name}
                                                </h6>
                                                <small>{contact.mobile_no}</small>

                                                <div className="mt-2 d-flex justify-content-center gap-3">
                                                    <i className="bi bi-eye-fill text-info" style={{ cursor: 'pointer' }} onClick={() => handleView(contact)} />
                                                    <i className="bi bi-pencil-square text-warning" style={{ cursor: 'pointer' }} onClick={() => handleEdit(contact)} />
                                                    <i className="bi bi-trash-fill text-danger" style={{ cursor: 'pointer' }} onClick={() => handleDelete(contact)} />
                                                    <i
                                                        className="bi bi-chat-dots-fill text-success"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            localStorage.setItem('chatContactId', contact.id);
                                                            window.location.href = '/apps/chat';
                                                        }}
                                                    />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            {!loading && contacts.length === 0 && (
                                <div className="text-center text-muted py-5">
                                    <i className="bi bi-person-x fs-1 d-block mb-2" />
                                    {search ? `No contacts match “${search}”` : 'No contacts found'}
                                </div>
                            )}

                            {/* PAGINATION */}
                            {lastPage > 1 && (
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                                    <small className="text-muted">
                                        Showing {firstShown.toLocaleString()}–{lastShown.toLocaleString()} of {total.toLocaleString()}
                                    </small>
                                    <div className="d-flex align-items-center gap-1">
                                        <Button size="sm" variant="light" disabled={page === 1 || loading} onClick={() => setPage(1)}>
                                            «
                                        </Button>
                                        <Button size="sm" variant="light" disabled={page === 1 || loading} onClick={() => setPage((p) => p - 1)}>
                                            ‹
                                        </Button>
                                        {pageWindow(page, lastPage).map((p, i) =>
                                            p === '…' ? (
                                                <span key={`gap-${i}`} className="px-1 text-muted">
                                                    …
                                                </span>
                                            ) : (
                                                <Button
                                                    key={p}
                                                    size="sm"
                                                    variant={p === page ? 'primary' : 'light'}
                                                    disabled={loading}
                                                    onClick={() => setPage(p)}
                                                >
                                                    {p}
                                                </Button>
                                            )
                                        )}
                                        <Button size="sm" variant="light" disabled={page === lastPage || loading} onClick={() => setPage((p) => p + 1)}>
                                            ›
                                        </Button>
                                        <Button size="sm" variant="light" disabled={page === lastPage || loading} onClick={() => setPage(lastPage)}>
                                            »
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* VIEW MODAL */}
                <Modal show={showViewModal} onHide={() => setShowViewModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Contact Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center">
                        {selectedContact && (
                            <>
                                <Image src={selectedContact.profileUrl} roundedCircle width={120} height={120} className="mb-3" style={{ objectFit: 'cover' }} />
                                <h5>{selectedContact.name}</h5>
                                <p>{selectedContact.mobile_no}</p>
                                <p>{selectedContact.group_name}</p>
                            </>
                        )}
                    </Modal.Body>
                </Modal>

                {/* EDIT MODAL */}
                <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                    <Modal.Header style={{ background: 'linear-gradient(135deg, #0eb7eb, #167491)' }}>
                        <Modal.Title className="text-white">Edit Contact</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {editedContact && (
                            <Form>
                                <Form.Group className="mb-3 text-center">
                                    <Image
                                        src={previewUrl || editedContact.profileUrl}
                                        roundedCircle
                                        width={120}
                                        height={120}
                                        className="mb-3"
                                        style={{ objectFit: 'cover' }}
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = FALLBACK_AVATAR;
                                        }}
                                    />
                                    <Form.Control type="file" accept="image/*" onChange={handleFileChange} className="mb-3" />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control type="text" name="name" value={editedContact.name} onChange={handleInputChange} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Mobile Number</Form.Label>
                                    <Form.Control type="text" name="mobile_no" value={editedContact.mobile_no} onChange={handleInputChange} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Group Name</Form.Label>
                                    <Form.Control type="text" name="group_name" value={editedContact.group_name} onChange={handleInputChange} />
                                </Form.Group>
                            </Form>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="success" onClick={handleSaveEdit}>
                            Save Changes
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Row>
        </>
    );
};

export default Test_Contact;
